import React, { useState, useEffect, useMemo } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, X, ChevronRight, Search, Package, Save, BookOpen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import fetchWithAuth from '../../services/fetchWithAuth';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('access_token');

/* Category icons as small SVGs */
const CategoryIcon = ({ code, size = 18 }) => {
  const s = size;
  if (code === 'bolt') return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12,2 20,7 20,17 12,22 4,17 4,7" />
      <line x1="12" y1="8" x2="12" y2="16" />
    </svg>
  );
  if (code === 'nut') return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="12,2 20,7 20,17 12,22 4,17 4,7" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
  if (code === 'washer') return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="4" />
    </svg>
  );
  // pin
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="3" x2="12" y2="21" />
      <line x1="8" y1="6" x2="16" y2="6" />
      <line x1="9" y1="18" x2="15" y2="18" />
    </svg>
  );
};

export const FastenerSelector = ({ onAddFastener, onApplyTemplate, onClose, insertionLabel }) => {
  const [ref, setRef] = useState(null);
  const [loading, setLoading] = useState(true);

  // Selection state
  const [category, setCategory] = useState(null);
  const [subType, setSubType] = useState('');
  const [size, setSize] = useState('');
  const [material, setMaterial] = useState('');
  const [lengthMm, setLengthMm] = useState('');
  const [threadType, setThreadType] = useState('Coarse');
  const [finePitch, setFinePitch] = useState('');
  const [finish, setFinish] = useState('plain');
  const [standard, setStandard] = useState('');
  const [customStandard, setCustomStandard] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [sizeSearch, setSizeSearch] = useState('');

  // Live lengths from library
  const [liveLengths, setLiveLengths] = useState([]);
  const [lengthsLoading, setLengthsLoading] = useState(false);
  const [showAddLength, setShowAddLength] = useState(false);
  const [customLength, setCustomLength] = useState('');
  const [addingLength, setAddingLength] = useState(false);

  // Set mode state — values are per-set quantities (0 = excluded)
  const [setMode, setSetMode] = useState(false);
  const [setComponents, setSetComponents] = useState({
    bolt: 1,
    nut: 1,
    spring_washer: 1,
    plain_washer: 1,
  });
  // Custom fastener creation
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customDesc, setCustomDesc] = useState('');
  // Template state
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showSaveTemplate, setShowSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templateDesc, setTemplateDesc] = useState('');
  const [applyingTemplate, setApplyingTemplate] = useState(null);
  // Half thread option
  const [threadCoverage, setThreadCoverage] = useState('full');
  // Inline creation states
  const [showCreateMaterial, setShowCreateMaterial] = useState(false);
  const [newMatCode, setNewMatCode] = useState('');
  const [newMatName, setNewMatName] = useState('');
  const [newMatGroup, setNewMatGroup] = useState('');
  const [showCreateFinish, setShowCreateFinish] = useState(false);
  const [newFinCode, setNewFinCode] = useState('');
  const [newFinName, setNewFinName] = useState('');
  const [showCreateSize, setShowCreateSize] = useState(false);
  const [newSizeStr, setNewSizeStr] = useState('');
  const [newSizePitch, setNewSizePitch] = useState('');
  const [showCreateSubType, setShowCreateSubType] = useState(false);
  const [newSTCode, setNewSTCode] = useState('');
  const [newSTName, setNewSTName] = useState('');
  const [newSTStandard, setNewSTStandard] = useState('');
  const [inlineCreating, setInlineCreating] = useState(false);

  // Refresh reference data after inline creation
  const refreshRef = async () => {
    const tk = getToken();
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/fasteners/reference`, { headers: { Authorization: `Bearer ${tk}` } });
      if (res.ok) setRef(await res.json());
    } catch {}
  };

  const handleInlineCreate = async (type) => {
    setInlineCreating(true);
    const tk = getToken();
    try {
      let url = '', body = {};
      if (type === 'material') {
        url = `${API}/api/v1/bom/fasteners/add-material`;
        body = { code: newMatCode.trim().toLowerCase().replace(/\s+/g, '_'), name: newMatName.trim(), group: newMatGroup.trim() };
      } else if (type === 'finish') {
        url = `${API}/api/v1/bom/fasteners/add-finish`;
        body = { code: newFinCode.trim().toLowerCase().replace(/\s+/g, '_'), name: newFinName.trim() };
      } else if (type === 'size') {
        url = `${API}/api/v1/bom/fasteners/add-size`;
        body = { size: newSizeStr.trim(), coarse_pitch: newSizePitch ? parseFloat(newSizePitch) : null, fine_pitches: [] };
      } else if (type === 'subtype') {
        url = `${API}/api/v1/bom/fasteners/add-subtype`;
        body = { category: category || 'BOLT', code: newSTCode.trim().toUpperCase(), name: newSTName.trim(), default_standard: newSTStandard.trim() };
      }
      const res = await fetchWithAuth(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` }, body: JSON.stringify(body) });
      const result = await res.json();
      if (res.ok) {
        const label = type === 'material' ? newMatName : type === 'finish' ? newFinName : type === 'size' ? newSizeStr : newSTName;
        toast.success(result.status === 'already_exists' ? `"${label}" already exists` : `"${label}" added to Fastener Library`);
        await refreshRef();
        // Auto-select the newly created item
        if (type === 'material') { setMaterial(body.code); setShowCreateMaterial(false); setNewMatCode(''); setNewMatName(''); setNewMatGroup(''); }
        if (type === 'finish') { setFinish(body.code); setShowCreateFinish(false); setNewFinCode(''); setNewFinName(''); }
        if (type === 'size') { setSize(body.size); setShowCreateSize(false); setNewSizeStr(''); setNewSizePitch(''); }
        if (type === 'subtype') { setSubType(body.code); setShowCreateSubType(false); setNewSTCode(''); setNewSTName(''); setNewSTStandard(''); }
      } else { toast.error(result.detail || 'Create failed'); }
    } catch (e) { toast.error(e.message || 'Create failed'); }
    setInlineCreating(false);
  };

  // Load reference data + templates
  const fetchTemplates = () => {
    const tk = getToken();
    fetchWithAuth(`${API}/api/v1/bom/fastener-set-templates`, {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(r => r.json())
      .then(data => setTemplates(data || []))
      .catch(() => {});
  };

  useEffect(() => {
    const tk = getToken();
    fetchWithAuth(`${API}/api/v1/bom/fasteners/reference`, {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(r => r.json())
      .then(data => { setRef(data); setLoading(false); })
      .catch(() => { toast.error('Failed to load fastener reference'); setLoading(false); });
    fetchTemplates();
  }, []);

  // Derived values
  const selectedCategory = useMemo(() => ref?.categories?.find(c => c.code === category), [ref, category]);
  const subTypes = useMemo(() => (category && ref?.sub_types?.[category]) || [], [ref, category]);
  const selectedSubType = useMemo(() => subTypes.find(s => s.code === subType), [subTypes, subType]);
  const selectedSize = useMemo(() => ref?.sizes?.find(s => s.size === size), [ref, size]);
  const selectedMaterial = useMemo(() => ref?.materials?.find(m => m.code === material), [ref, material]);

  // Auto-set default standard when sub-type changes
  useEffect(() => {
    if (selectedSubType?.default_standard) {
      setStandard(selectedSubType.default_standard);
    }
  }, [selectedSubType]);

  // Reset dependent fields when category changes
  useEffect(() => {
    setSubType('');
    setSize('');
    setLengthMm('');
    setThreadType('Coarse');
    setFinePitch('');
    setStandard('');
  }, [category]);

  // Reset length when size changes
  useEffect(() => { setLengthMm(''); setShowAddLength(false); setCustomLength(''); }, [size]);

  // Fetch live lengths from library when size changes
  useEffect(() => {
    if (!size) { setLiveLengths([]); return; }
    const tk = getToken();
    setLengthsLoading(true);
    fetchWithAuth(`${API}/api/v1/bom/fasteners/live-lengths?size=${size}`, {
      headers: { Authorization: `Bearer ${tk}` },
    })
      .then(r => r.json())
      .then(data => {
        setLiveLengths(data?.lengths || []);
        setLengthsLoading(false);
      })
      .catch(() => { setLiveLengths([]); setLengthsLoading(false); });
  }, [size]);

  // Add custom length to library
  const handleAddCustomLength = async () => {
    const val = parseInt(customLength);
    if (!val || val <= 0 || !size) return;
    setAddingLength(true);
    const tk = getToken();
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/fasteners/add-length`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({
          size,
          length_mm: val,
          type_code: subType || 'HEX',
          material_code: material || '',
          standard: standard || '',
        }),
      });
      const result = await res.json();
      if (res.ok) {
        toast.success(result.status === 'already_exists'
          ? `${val}mm already exists in library`
          : `${val}mm added to Fasteners Library`);
        setLengthMm(String(val));
        setShowAddLength(false);
        setCustomLength('');
        // Refresh live lengths
        const r2 = await fetchWithAuth(`${API}/api/v1/bom/fasteners/live-lengths?size=${size}`, {
          headers: { Authorization: `Bearer ${tk}` },
        });
        const d2 = await r2.json();
        setLiveLengths(d2?.lengths || []);
      } else {
        toast.error(result.detail || 'Failed to add length');
      }
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setAddingLength(false); }
  };

  // Build preview description
  const previewDesc = useMemo(() => {
    if (!size || !selectedSubType) return '';
    const parts = [size];
    if (lengthMm) parts[0] = `${size} x ${lengthMm}`;
    parts.push(selectedSubType.name);
    if (selectedMaterial) parts.push(selectedMaterial.name);
    if (selectedCategory?.has_thread && threadType) {
      const pitch = threadType === 'Coarse' ? selectedSize?.coarse_pitch : (finePitch || selectedSize?.fine_pitches?.[0]);
      parts.push(`${threadType}${pitch ? ` (${pitch}mm)` : ''}`);
      if (threadCoverage === 'half') parts.push('Half Thread');
    }
    const finishObj = ref?.finishes?.find(f => f.code === finish);
    if (finishObj && finish !== 'plain') parts.push(finishObj.name);
    const std = standard === '__custom__' ? customStandard : standard;
    if (std && std !== '__custom__') parts.push(`[${std}]`);
    return parts.join(', ');
  }, [size, lengthMm, selectedSubType, selectedMaterial, selectedCategory, threadType, selectedSize, finePitch, finish, ref, standard, customStandard]);

  const lengthRequired = selectedCategory?.has_length || setMode;
  const lengthSatisfied = !lengthRequired || (lengthMm && String(lengthMm).trim() !== '');

  const canSubmit = setMode
    ? (size && material && lengthSatisfied && Object.values(setComponents).some(v => v > 0))
    : (category && subType && size && material && lengthSatisfied);

  const buildPayload = (cat, subTypeCode, subTypeName, defaultStd, hasLength, hasThread) => {
    const finalStandard = standard === '__custom__' ? customStandard.trim() : (defaultStd || standard || '');
    const pitch = threadType === 'Coarse'
      ? selectedSize?.coarse_pitch
      : (finePitch ? parseFloat(finePitch) : selectedSize?.fine_pitches?.[0]);
    return {
      category: cat,
      type_code: subTypeCode,
      sub_type: subTypeCode,
      sub_type_name: subTypeName,
      size,
      length_mm: hasLength && lengthMm ? parseInt(lengthMm) : null,
      thread_type: hasThread ? threadType : null,
      thread_pitch_mm: hasThread ? pitch : null,
      thread_coverage: hasThread ? threadCoverage : null,
      material: material,
      material_code: material,
      material_name: selectedMaterial?.name || material,
      material_group: selectedMaterial?.group || '',
      finish: ref?.finishes?.find(f => f.code === finish)?.name || 'Plain',
      finish_code: finish,
      finish_name: ref?.finishes?.find(f => f.code === finish)?.name || 'Plain',
      standard: finalStandard,
      quantity,
    };
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);

    try {
      if (setMode) {
        // Add each selected component — quantity = per-component qty × number of sets
        const items = [];
        if (setComponents.bolt > 0) {
          const boltSubType = subType || 'HEX';
          const boltSubTypeName = selectedSubType?.name || 'Hex Head';
          const boltStd = selectedSubType?.default_standard || 'ISO 4014';
          const p = buildPayload('BOLT', boltSubType, boltSubTypeName, boltStd, true, true);
          p.quantity = setComponents.bolt * quantity;
          items.push({ label: `${size}${lengthMm ? ' x ' + lengthMm : ''} ${boltSubTypeName} ×${setComponents.bolt}`, payload: p });
        }
        if (setComponents.plain_washer > 0) {
          const p = buildPayload('WASHER', 'FLAT', 'Flat Washer', 'ISO 7089', false, false);
          p.quantity = setComponents.plain_washer * quantity;
          items.push({ label: `${size} Plain Washer ×${setComponents.plain_washer}`, payload: p });
        }
        if (setComponents.spring_washer > 0) {
          const p = buildPayload('WASHER', 'SPRG', 'Spring/Lock', 'DIN 127', false, false);
          p.quantity = setComponents.spring_washer * quantity;
          items.push({ label: `${size} Spring Washer ×${setComponents.spring_washer}`, payload: p });
        }
        if (setComponents.nut > 0) {
          const p = buildPayload('NUT', 'HEXN', 'Hex Nut', 'ISO 4032', false, true);
          p.quantity = setComponents.nut * quantity;
          items.push({ label: `${size} Hex Nut ×${setComponents.nut}`, payload: p });
        }

        for (const item of items) {
          await onAddFastener(item.payload);
        }
        toast.success(`Fastener set (×${quantity}) added: ${items.length} types (${items.map(i => i.label).join(' + ')})`);
      } else {
        const payload = buildPayload(
          category,
          subType,
          selectedSubType?.name || subType,
          null,
          selectedCategory?.has_length,
          selectedCategory?.has_thread
        );
        if (!setMode && standard !== '__custom__') payload.standard = standard === '__none__' ? '' : standard;
        await onAddFastener(payload);
        toast.success(`Added: ${previewDesc}`);
      }

      // Reset for next add (keep category & set mode)
      setSubType('');
      setSize('');
      setLengthMm('');
      setThreadType('Coarse');
      setFinePitch('');
      setFinish('plain');
      setStandard('');
      setCustomStandard('');
      setQuantity(1);
    } catch (e) {
      toast.error(e.message || 'Failed to add fastener');
    } finally {
      setSubmitting(false);
    }
  };

  // Save current set configuration as a template
  const handleSaveTemplate = async () => {
    if (!templateName.trim()) { toast.error('Template name is required'); return; }
    if (!size || !material) { toast.error('Select size and material first'); return; }
    const tk = getToken();
    const boltSubType = subType || 'HEX';
    const boltSubTypeName = selectedSubType?.name || 'Hex Head';
    const boltStd = selectedSubType?.default_standard || 'ISO 4014';
    const matName = selectedMaterial?.name || material;
    const matGroup = selectedMaterial?.group || '';
    const finishName = ref?.finishes?.find(f => f.code === finish)?.name || 'Plain';

    const components = [];
    if (setComponents.bolt > 0) {
      components.push({
        category: 'BOLT', type_code: boltSubType, sub_type: boltSubType, sub_type_name: boltSubTypeName,
        size, length_mm: lengthMm ? parseInt(lengthMm) : null,
        thread_type: threadType, material, material_code: material, material_name: matName, material_group: matGroup,
        finish: finishName, finish_code: finish, finish_name: finishName, standard: boltStd,
        qty_per_set: setComponents.bolt,
      });
    }
    if (setComponents.plain_washer > 0) {
      components.push({
        category: 'WASHER', type_code: 'FLAT', sub_type: 'FLAT', sub_type_name: 'Flat Washer',
        size, material, material_code: material, material_name: matName, material_group: matGroup,
        finish: finishName, finish_code: finish, finish_name: finishName, standard: 'ISO 7089',
        qty_per_set: setComponents.plain_washer,
      });
    }
    if (setComponents.spring_washer > 0) {
      components.push({
        category: 'WASHER', type_code: 'SPRG', sub_type: 'SPRG', sub_type_name: 'Spring/Lock',
        size, material, material_code: material, material_name: matName, material_group: matGroup,
        finish: finishName, finish_code: finish, finish_name: finishName, standard: 'DIN 127',
        qty_per_set: setComponents.spring_washer,
      });
    }
    if (setComponents.nut > 0) {
      components.push({
        category: 'NUT', type_code: 'HEXN', sub_type: 'HEXN', sub_type_name: 'Hex Nut',
        size, thread_type: threadType, material, material_code: material, material_name: matName, material_group: matGroup,
        finish: finishName, finish_code: finish, finish_name: finishName, standard: 'ISO 4032',
        qty_per_set: setComponents.nut,
      });
    }
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/fastener-set-templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${tk}` },
        body: JSON.stringify({ template_name: templateName.trim(), description: templateDesc.trim(), components }),
      });
      if (res.ok) {
        toast.success(`Template "${templateName.trim()}" saved with ${components.length} components`);
        setShowSaveTemplate(false);
        setTemplateName('');
        setTemplateDesc('');
        fetchTemplates();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save template');
      }
    } catch (e) { toast.error(e.message); }
  };

  // Apply a template to BOM
  const handleApplyTemplate = async (tmpl) => {
    setApplyingTemplate(tmpl.template_id);
    try {
      await onApplyTemplate(tmpl.template_id, quantity);
      toast.success(`Applied "${tmpl.template_name}" — ${tmpl.components.length} fasteners added`);
    } catch (e) {
      toast.error(e.message || 'Failed to apply template');
    } finally {
      setApplyingTemplate(null);
    }
  };

  // Delete a template
  const handleDeleteTemplate = async (tmplId) => {
    const tk = getToken();
    try {
      const res = await fetchWithAuth(`${API}/api/v1/bom/fastener-set-templates/${tmplId}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${tk}` },
      });
      if (res.ok) { toast.success('Template deleted'); fetchTemplates(); }
    } catch (e) { toast.error(e.message); }
  };

  if (loading) return (
    <div className="px-3 py-2 bg-amber-900/10 border-b border-amber-700/30 text-[10px] text-amber-400 animate-pulse">
      Loading fastener reference data...
    </div>
  );

  const filteredSizes = ref?.sizes?.filter(s =>
    !sizeSearch || s.size.toLowerCase().includes(sizeSearch.toLowerCase())
  ) || [];

  return (
    <div className="px-3 py-2.5 bg-amber-900/10 border-b border-amber-700/30 animate-in fade-in duration-150" data-testid="fastener-selector-panel">
      {/* Header */}
      <div className="flex items-center gap-2 mb-2">
        <CategoryIcon code="bolt" size={14} />
        <span className="text-[10px] text-amber-400 font-semibold uppercase tracking-wider">Add Fastener</span>
        {insertionLabel && <span className="text-[10px] text-slate-500">under {insertionLabel}</span>}
        {/* Set Mode Toggle */}
        <button
          onClick={() => { setSetMode(!setMode); setShowTemplates(false); if (!setMode) setCategory('BOLT'); }}
          className={`ml-2 flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-medium border transition-all ${
            setMode
              ? 'border-emerald-500 bg-emerald-900/40 text-emerald-300 ring-1 ring-emerald-500/30'
              : 'border-slate-600 bg-slate-800/50 text-slate-400 hover:text-slate-200 hover:border-slate-500'
          }`}
          data-testid="fastener-set-mode-toggle"
        >
          <Package className="w-3 h-3" /> {setMode ? 'Set Mode ON' : 'Add as Set'}
        </button>
        {/* Templates Toggle */}
        <button
          onClick={() => { setShowTemplates(!showTemplates); setShowSaveTemplate(false); }}
          className={`flex items-center gap-1 px-2 py-0.5 rounded text-[9px] font-medium border transition-all ${
            showTemplates
              ? 'border-violet-500 bg-violet-900/40 text-violet-300 ring-1 ring-violet-500/30'
              : 'border-slate-600 bg-slate-800/50 text-slate-400 hover:text-slate-200 hover:border-slate-500'
          }`}
          data-testid="fastener-templates-toggle"
        >
          <BookOpen className="w-3 h-3" /> Templates{templates.length > 0 ? ` (${templates.length})` : ''}
        </button>
        <button onClick={onClose} className="ml-auto text-slate-500 hover:text-slate-300" data-testid="fastener-close-btn">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Set Mode: Component toggles with per-component quantity */}
      {setMode && (
        <div className="mb-2 p-2 rounded border border-emerald-700/30 bg-emerald-900/10" data-testid="fastener-set-config">
          <div className="text-[9px] text-emerald-400 font-semibold uppercase tracking-wider mb-1.5">Fastener Set Components (qty per set)</div>
          <div className="flex gap-2 mb-1.5 flex-wrap">
            {[
              { key: 'bolt', label: 'Bolt', icon: 'bolt', maxQty: 1 },
              { key: 'plain_washer', label: 'Plain Washer', icon: 'washer', maxQty: 4 },
              { key: 'spring_washer', label: 'Spring Washer', icon: 'washer', maxQty: 4 },
              { key: 'nut', label: 'Hex Nut', icon: 'nut', maxQty: 4 },
            ].map(comp => {
              const qty = setComponents[comp.key];
              const isActive = qty > 0;
              return (
                <div key={comp.key} className={`flex items-center gap-1 px-2 py-1 rounded border text-[10px] transition-all ${
                  isActive
                    ? 'border-emerald-500/60 bg-emerald-900/30 text-emerald-300'
                    : 'border-slate-700/30 bg-slate-800/20 text-slate-600'
                }`} data-testid={`fastener-set-${comp.key}`}>
                  <button onClick={() => setSetComponents(prev => ({ ...prev, [comp.key]: prev[comp.key] > 0 ? 0 : 1 }))}
                    className="flex items-center gap-1">
                    <CategoryIcon code={comp.icon} size={12} />
                    <span className={!isActive ? 'line-through' : ''}>{comp.label}</span>
                  </button>
                  {isActive && (
                    <div className="flex items-center gap-0.5 ml-1">
                      <button onClick={() => setSetComponents(prev => ({ ...prev, [comp.key]: Math.max(1, prev[comp.key] - 1) }))}
                        className="w-4 h-4 flex items-center justify-center rounded bg-slate-700/50 hover:bg-slate-600 text-slate-300 text-[10px]"
                        data-testid={`fastener-set-${comp.key}-minus`}>-</button>
                      <span className="font-mono w-4 text-center text-emerald-200 font-semibold">{qty}</span>
                      <button onClick={() => setSetComponents(prev => ({ ...prev, [comp.key]: Math.min(comp.maxQty, prev[comp.key] + 1) }))}
                        className="w-4 h-4 flex items-center justify-center rounded bg-slate-700/50 hover:bg-slate-600 text-slate-300 text-[10px]"
                        data-testid={`fastener-set-${comp.key}-plus`}>+</button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="text-[8px] text-slate-500 flex items-center gap-1">
            <span className="text-amber-500">AS9100D / Defense std:</span>
            <span>Bolt ×1 + Plain Washer ×2 (head + nut side) + Spring Washer ×1 + Nut ×1 recommended</span>
          </div>
        </div>
      )}

      {/* Templates Browser Panel */}
      {showTemplates && (
        <div className="mb-2 p-2 rounded border border-violet-700/30 bg-violet-900/10" data-testid="fastener-templates-panel">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[9px] text-violet-400 font-semibold uppercase tracking-wider">Saved Fastener Set Templates</span>
            <span className="text-[8px] text-slate-600">Apply with one click</span>
          </div>
          {templates.length === 0 ? (
            <div className="text-[10px] text-slate-500 text-center py-3">
              No templates saved yet. Use Set Mode to configure a fastener set, then save it as a template.
            </div>
          ) : (
            <div className="space-y-1 max-h-[140px] overflow-y-auto">
              {templates.map(tmpl => (
                <div key={tmpl.template_id}
                  className="flex items-center gap-2 px-2 py-1.5 rounded border border-slate-700/30 bg-slate-800/30 hover:border-violet-600/40 transition-all group"
                  data-testid={`fastener-template-${tmpl.template_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] text-white font-medium truncate">{tmpl.template_name}</div>
                    <div className="text-[8px] text-slate-500 truncate">
                      {tmpl.components?.length || 0} items: {tmpl.components?.map(c => `${c.size} ${c.sub_type_name || c.sub_type}`).join(' + ')}
                    </div>
                    {tmpl.description && <div className="text-[8px] text-slate-600 truncate">{tmpl.description}</div>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Input type="number" min={1} defaultValue={1}
                      className="h-6 w-10 text-[9px] bg-slate-900 border-slate-600 text-white text-center"
                      data-testid={`template-qty-${tmpl.template_id}`}
                      id={`tmpl-qty-${tmpl.template_id}`} />
                    <Button size="sm"
                      className="h-6 px-2 text-[9px] bg-violet-600 hover:bg-violet-500 text-white"
                      disabled={applyingTemplate === tmpl.template_id}
                      onClick={() => {
                        const qtyEl = document.getElementById(`tmpl-qty-${tmpl.template_id}`);
                        const qty = parseInt(qtyEl?.value) || 1;
                        setQuantity(qty);
                        handleApplyTemplate({ ...tmpl });
                      }}
                      data-testid={`template-apply-${tmpl.template_id}`}>
                      {applyingTemplate === tmpl.template_id ? '...' : 'Apply'}
                    </Button>
                    <button onClick={() => handleDeleteTemplate(tmpl.template_id)}
                      className="text-slate-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`template-delete-${tmpl.template_id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Category cards — 5-column wrapping grid, uniform size */}
      {!setMode && (
        <div className="grid grid-cols-5 gap-1.5 mb-2" data-testid="fastener-category-grid">
          {ref?.categories?.map(cat => (
            <button
              key={cat.code}
              onClick={() => setCategory(cat.code)}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded border text-[10px] font-medium transition-all truncate ${
                category === cat.code
                  ? 'border-amber-500 bg-amber-900/40 text-amber-300 ring-1 ring-amber-500/30'
                  : 'border-slate-700/40 bg-slate-800/30 text-slate-400 hover:border-slate-500 hover:text-slate-200'
              }`}
              data-testid={`fastener-cat-${cat.code}`}
              title={cat.name}
            >
              <CategoryIcon code={cat.code?.toLowerCase()} size={14} />
              <span className="truncate">{cat.name}</span>
            </button>
          ))}
        </div>
      )}

      {/* Step 2+3: Type, Size, Material, Length, Thread — in rows */}
      {(category || setMode) && (
        <div className="space-y-2">
          {/* Row 1: Sub-type (bolt type for sets) + Size */}
          <div className="flex gap-2">
            <div className="flex-1 min-w-0">
              <Label className="text-[9px] text-slate-500 mb-0.5 block uppercase tracking-wider">
                {setMode ? 'Bolt Type' : 'Type'}
              </Label>
              <Select value={subType} onValueChange={v => { if (v === '__create__') { setShowCreateSubType(true); } else { setSubType(v); setShowCreateSubType(false); } }} data-testid="fastener-subtype-select">
                <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-600 text-white" data-testid="fastener-subtype-trigger">
                  <SelectValue placeholder={setMode ? "Hex Head Bolt (default)" : "Select type..."} />
                </SelectTrigger>
                <SelectContent>
                  {(setMode ? (ref?.sub_types?.['BOLT'] || []) : subTypes).map(st => (
                    <SelectItem key={st.code} value={st.code}>{st.name}</SelectItem>
                  ))}
                  <SelectItem value="__create__"><Plus className="w-3 h-3 inline mr-1" />Create New</SelectItem>
                </SelectContent>
              </Select>
              {showCreateSubType && (
                <div className="mt-1 p-1.5 rounded border border-amber-600/30 bg-slate-900 space-y-1 animate-in fade-in duration-100">
                  <div className="flex gap-1">
                    <Input value={newSTCode} onChange={e => setNewSTCode(e.target.value)} placeholder="Code (e.g. CARR)" className="h-5 text-[9px] bg-slate-800 border-slate-600 text-white flex-1" autoFocus />
                    <Input value={newSTName} onChange={e => setNewSTName(e.target.value)} placeholder="Name" className="h-5 text-[9px] bg-slate-800 border-slate-600 text-white flex-1" />
                  </div>
                  <div className="flex gap-1">
                    <Input value={newSTStandard} onChange={e => setNewSTStandard(e.target.value)} placeholder="Standard (opt.)" className="h-5 text-[9px] bg-slate-800 border-slate-600 text-white flex-1" />
                    <button onClick={() => handleInlineCreate('subtype')} disabled={!newSTCode || !newSTName || inlineCreating} className="h-5 px-2 rounded text-[8px] bg-amber-600 text-white disabled:opacity-40">{inlineCreating ? '...' : 'Add'}</button>
                    <button onClick={() => setShowCreateSubType(false)} className="text-slate-500 hover:text-slate-300"><X className="w-3 h-3" /></button>
                  </div>
                </div>
              )}
            </div>
            <div className="w-[160px] shrink-0">
              <Label className="text-[9px] text-slate-500 mb-0.5 block uppercase tracking-wider">Size</Label>
              <div className="relative">
                <div className="flex flex-wrap gap-0.5 max-h-[60px] overflow-y-auto rounded border border-slate-600 bg-slate-800 p-0.5" data-testid="fastener-size-grid">
                  {filteredSizes.map(s => (
                    <button key={s.size} onClick={() => setSize(s.size)}
                      className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-all ${
                        size === s.size
                          ? 'bg-amber-600 text-white font-bold'
                          : 'text-slate-400 hover:text-white hover:bg-slate-700'
                      }`}
                      data-testid={`fastener-size-${s.size}`}
                    >
                      {s.size}
                    </button>
                  ))}
                  {!showCreateSize && (
                    <button onClick={() => setShowCreateSize(true)}
                      className="px-1.5 py-0.5 rounded text-[9px] font-mono text-amber-400/70 hover:text-amber-300 hover:bg-amber-900/30 border border-dashed border-amber-600/30 transition-all"
                      data-testid="fastener-add-size-btn" title="Add a new size">+</button>
                  )}
                </div>
                {showCreateSize && (
                  <div className="flex items-center gap-1 mt-1 animate-in fade-in duration-100">
                    <Input value={newSizeStr} onChange={e => setNewSizeStr(e.target.value)} placeholder="e.g. M30" className="h-5 w-14 text-[9px] bg-slate-900 border-amber-600/40 text-white" autoFocus />
                    <Input value={newSizePitch} onChange={e => setNewSizePitch(e.target.value)} placeholder="Pitch" className="h-5 w-12 text-[9px] bg-slate-900 border-amber-600/40 text-white" />
                    <button onClick={() => handleInlineCreate('size')} disabled={!newSizeStr || inlineCreating} className="h-5 px-2 rounded text-[8px] bg-amber-600 text-white disabled:opacity-40">{inlineCreating ? '...' : 'Add'}</button>
                    <button onClick={() => { setShowCreateSize(false); setNewSizeStr(''); setNewSizePitch(''); }} className="text-slate-500 hover:text-slate-300"><X className="w-3 h-3" /></button>
                  </div>
                )}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <Label className="text-[9px] text-slate-500 mb-0.5 block uppercase tracking-wider">Material</Label>
              <Select value={material} onValueChange={v => { if (v === '__create__') { setShowCreateMaterial(true); } else { setMaterial(v); setShowCreateMaterial(false); } }}>
                <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-600 text-white" data-testid="fastener-material-trigger">
                  <SelectValue placeholder="Select material..." />
                </SelectTrigger>
                <SelectContent>
                  {ref?.materials?.map(m => (
                    <SelectItem key={m.code} value={m.code}>
                      <span>{m.name}</span>
                      <span className="text-[9px] text-slate-500 ml-1">({m.group})</span>
                    </SelectItem>
                  ))}
                  <SelectItem value="__create__"><Plus className="w-3 h-3 inline mr-1" />Create New</SelectItem>
                </SelectContent>
              </Select>
              {showCreateMaterial && (
                <div className="mt-1 p-1.5 rounded border border-amber-600/30 bg-slate-900 space-y-1 animate-in fade-in duration-100">
                  <Input value={newMatName} onChange={e => setNewMatName(e.target.value)} placeholder="Name (e.g. Titanium Gr.5)" className="h-5 text-[9px] bg-slate-800 border-slate-600 text-white" autoFocus />
                  <div className="flex gap-1">
                    <Input value={newMatCode} onChange={e => setNewMatCode(e.target.value)} placeholder="Code (e.g. ti_gr5)" className="h-5 text-[9px] bg-slate-800 border-slate-600 text-white flex-1" />
                    <Input value={newMatGroup} onChange={e => setNewMatGroup(e.target.value)} placeholder="Group (e.g. Titanium)" className="h-5 text-[9px] bg-slate-800 border-slate-600 text-white flex-1" />
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleInlineCreate('material')} disabled={!newMatCode || !newMatName || inlineCreating} className="h-5 px-2 rounded text-[8px] bg-amber-600 text-white disabled:opacity-40 flex-1">{inlineCreating ? '...' : 'Add Material'}</button>
                    <button onClick={() => setShowCreateMaterial(false)} className="text-slate-500 hover:text-slate-300"><X className="w-3 h-3" /></button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Row 2: Length (if applicable) + Thread + Finish + Standard */}
          <div className="flex gap-2 items-end">
            {(setMode || selectedCategory?.has_length) && size && (
              <div className="min-w-[180px] max-w-[280px] shrink-0">
                <div className="flex items-center gap-1 mb-0.5">
                  <Label className="text-[9px] text-slate-500 uppercase tracking-wider">
                    Length (mm) <span className="text-red-400">*</span>
                  </Label>
                  {lengthsLoading && <span className="text-[8px] text-slate-600 animate-pulse">loading...</span>}
                  {!lengthsLoading && liveLengths.length > 0 && (
                    <span className="text-[8px] text-slate-600">{liveLengths.length} available</span>
                  )}
                </div>
                {liveLengths.length > 0 ? (
                  <div className="relative">
                    <div className="flex flex-wrap gap-[3px] max-h-[52px] overflow-y-auto rounded border border-slate-600 bg-slate-800 p-[3px]" data-testid="fastener-length-grid">
                      {liveLengths.map(l => {
                        const isSelected = String(lengthMm) === String(l.length);
                        return (
                          <button key={l.length} onClick={() => setLengthMm(String(l.length))}
                            className={`px-1.5 py-0.5 rounded text-[9px] font-mono transition-all ${
                              isSelected
                                ? 'bg-amber-600 text-white font-bold ring-1 ring-amber-400/50'
                                : 'text-slate-300 hover:text-white hover:bg-slate-700 bg-slate-700/40'
                            }`}
                            data-testid={`fastener-length-${l.length}`}
                            title={`${l.length}mm — ${l.count} item${l.count > 1 ? 's' : ''} in library`}
                          >
                            {l.length}
                          </button>
                        );
                      })}
                      {/* Add custom length button */}
                      {!showAddLength && (
                        <button onClick={() => setShowAddLength(true)}
                          className="px-1.5 py-0.5 rounded text-[9px] font-mono text-amber-400/70 hover:text-amber-300 hover:bg-amber-900/30 border border-dashed border-amber-600/30 transition-all"
                          data-testid="fastener-add-length-btn"
                          title="Add a custom length not in the library"
                        >
                          +
                        </button>
                      )}
                    </div>
                    {/* Inline add-length input */}
                    {showAddLength && (
                      <div className="flex items-center gap-1 mt-1 animate-in fade-in duration-100" data-testid="fastener-add-length-form">
                        <Input
                          type="number" min={1} step={1}
                          value={customLength}
                          onChange={e => setCustomLength(e.target.value)}
                          placeholder="mm"
                          className="h-6 w-16 text-[10px] bg-slate-900 border-amber-600/40 text-white"
                          data-testid="fastener-custom-length-input"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter' && customLength) handleAddCustomLength(); if (e.key === 'Escape') setShowAddLength(false); }}
                        />
                        <button onClick={handleAddCustomLength}
                          disabled={!customLength || addingLength}
                          className="h-6 px-2 rounded text-[9px] font-medium bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40 transition-all"
                          data-testid="fastener-custom-length-submit"
                        >
                          {addingLength ? '...' : 'Add'}
                        </button>
                        <button onClick={() => { setShowAddLength(false); setCustomLength(''); }}
                          className="text-slate-500 hover:text-slate-300 text-[9px]"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                ) : lengthsLoading ? (
                  <div className="h-7 rounded border border-slate-600 bg-slate-800 flex items-center justify-center">
                    <span className="text-[9px] text-slate-600 animate-pulse">Loading lengths...</span>
                  </div>
                ) : (
                  /* No library lengths — show add-length UI */
                  <div className="relative">
                    <div className="flex flex-wrap gap-[3px] rounded border border-slate-700/50 bg-slate-800 p-[3px] items-center" data-testid="fastener-length-grid-empty">
                      <span className="text-[8px] text-slate-600 px-1">No lengths in library</span>
                      {!showAddLength && (
                        <button onClick={() => setShowAddLength(true)}
                          className="px-1.5 py-0.5 rounded text-[9px] font-mono text-amber-400/70 hover:text-amber-300 hover:bg-amber-900/30 border border-dashed border-amber-600/30 transition-all"
                          data-testid="fastener-add-first-length-btn"
                        >
                          + Add
                        </button>
                      )}
                    </div>
                    {showAddLength && (
                      <div className="flex items-center gap-1 mt-1 animate-in fade-in duration-100" data-testid="fastener-add-length-form-empty">
                        <Input
                          type="number" min={1} step={1}
                          value={customLength}
                          onChange={e => setCustomLength(e.target.value)}
                          placeholder="mm"
                          className="h-6 w-16 text-[10px] bg-slate-900 border-amber-600/40 text-white"
                          data-testid="fastener-custom-length-input-empty"
                          autoFocus
                          onKeyDown={e => { if (e.key === 'Enter' && customLength) handleAddCustomLength(); if (e.key === 'Escape') setShowAddLength(false); }}
                        />
                        <button onClick={handleAddCustomLength}
                          disabled={!customLength || addingLength}
                          className="h-6 px-2 rounded text-[9px] font-medium bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40 transition-all"
                          data-testid="fastener-custom-length-submit-empty"
                        >
                          {addingLength ? '...' : 'Add to Library'}
                        </button>
                        <button onClick={() => { setShowAddLength(false); setCustomLength(''); }}
                          className="text-slate-500 hover:text-slate-300 text-[9px]"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            {(setMode || selectedCategory?.has_thread) && size && (
              <div className="w-[180px] shrink-0">
                <Label className="text-[9px] text-slate-500 mb-0.5 block uppercase tracking-wider">Thread</Label>
                <div className="flex gap-0.5">
                  <button onClick={() => { setThreadType('Coarse'); setFinePitch(''); }}
                    className={`flex-1 h-7 rounded-l text-[9px] font-medium border transition-all ${
                      threadType === 'Coarse'
                        ? 'bg-amber-700/60 border-amber-600 text-amber-200'
                        : 'bg-slate-800 border-slate-600 text-slate-400 hover:text-slate-200'
                    }`}
                    data-testid="fastener-thread-coarse"
                  >
                    Coarse{selectedSize?.coarse_pitch ? ` (${selectedSize.coarse_pitch})` : ''}
                  </button>
                  {selectedSize?.fine_pitches?.length > 0 && (
                    <button onClick={() => setThreadType('Fine')}
                      className={`flex-1 h-7 rounded-r text-[9px] font-medium border transition-all ${
                        threadType === 'Fine'
                          ? 'bg-amber-700/60 border-amber-600 text-amber-200'
                          : 'bg-slate-800 border-slate-600 text-slate-400 hover:text-slate-200'
                      }`}
                      data-testid="fastener-thread-fine"
                    >
                      Fine
                    </button>
                  )}
                </div>
                {/* Half Thread / Full Thread toggle */}
                <div className="flex gap-0.5 mt-1">
                  <button onClick={() => setThreadCoverage('full')}
                    className={`flex-1 h-5 rounded-l text-[8px] font-medium border transition-all ${
                      threadCoverage === 'full' ? 'bg-blue-700/60 border-blue-600 text-blue-200' : 'bg-slate-800 border-slate-600 text-slate-500 hover:text-slate-300'
                    }`} data-testid="fastener-thread-full">Full Thread</button>
                  <button onClick={() => setThreadCoverage('half')}
                    className={`flex-1 h-5 rounded-r text-[8px] font-medium border transition-all ${
                      threadCoverage === 'half' ? 'bg-blue-700/60 border-blue-600 text-blue-200' : 'bg-slate-800 border-slate-600 text-slate-500 hover:text-slate-300'
                    }`} data-testid="fastener-thread-half">Half Thread</button>
                </div>
                {threadType === 'Fine' && selectedSize?.fine_pitches?.length > 1 && (
                  <div className="flex gap-0.5 mt-0.5">
                    {selectedSize.fine_pitches.map(p => (
                      <button key={p} onClick={() => setFinePitch(String(p))}
                        className={`px-1.5 py-0.5 rounded text-[8px] font-mono ${
                          finePitch === String(p)
                            ? 'bg-amber-700/60 text-amber-200'
                            : 'text-slate-500 hover:text-slate-300 bg-slate-800'
                        }`}>
                        {p}mm
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="w-[110px] shrink-0">
              <Label className="text-[9px] text-slate-500 mb-0.5 block uppercase tracking-wider">Finish</Label>
              <Select value={finish} onValueChange={v => { if (v === '__create__') { setShowCreateFinish(true); } else { setFinish(v); setShowCreateFinish(false); } }}>
                <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-600 text-white" data-testid="fastener-finish-trigger">
                  <SelectValue placeholder="Finish..." />
                </SelectTrigger>
                <SelectContent>
                  {ref?.finishes?.map(f => (
                    <SelectItem key={f.code} value={f.code}>{f.name}</SelectItem>
                  ))}
                  <SelectItem value="__create__"><Plus className="w-3 h-3 inline mr-1" />Create New</SelectItem>
                </SelectContent>
              </Select>
              {showCreateFinish && (
                <div className="mt-1 p-1.5 rounded border border-amber-600/30 bg-slate-900 space-y-1 animate-in fade-in duration-100">
                  <Input value={newFinName} onChange={e => setNewFinName(e.target.value)} placeholder="Name" className="h-5 text-[9px] bg-slate-800 border-slate-600 text-white" autoFocus />
                  <div className="flex gap-1">
                    <Input value={newFinCode} onChange={e => setNewFinCode(e.target.value)} placeholder="Code" className="h-5 text-[9px] bg-slate-800 border-slate-600 text-white flex-1" />
                    <button onClick={() => handleInlineCreate('finish')} disabled={!newFinCode || !newFinName || inlineCreating} className="h-5 px-2 rounded text-[8px] bg-amber-600 text-white disabled:opacity-40">{inlineCreating ? '...' : 'Add'}</button>
                    <button onClick={() => setShowCreateFinish(false)} className="text-slate-500 hover:text-slate-300"><X className="w-3 h-3" /></button>
                  </div>
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <Label className="text-[9px] text-slate-500 mb-0.5 block uppercase tracking-wider">Standard</Label>
              <Select value={standard} onValueChange={v => { setStandard(v); if (v !== '__custom__') setCustomStandard(''); }}>
                <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-600 text-white" data-testid="fastener-standard-trigger">
                  <SelectValue placeholder="Select standard..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__"><span className="text-slate-500">None / Not specified</span></SelectItem>
                  {ref?.common_standards?.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                  <SelectItem value="__custom__"><span className="text-amber-400">+ Enter custom standard</span></SelectItem>
                </SelectContent>
              </Select>
              {standard === '__custom__' && (
                <Input value={customStandard} onChange={e => setCustomStandard(e.target.value)}
                  placeholder="e.g. MIL-S-8879, NAS 1352..."
                  className="h-6 text-[10px] bg-slate-800 border-amber-600/40 text-white mt-0.5"
                  data-testid="fastener-custom-standard" autoFocus />
              )}
            </div>
          </div>

          {/* Preview + Submit row */}
          <div className="flex items-center gap-2 pt-1 border-t border-slate-700/30">
            {setMode && size && material ? (
              <div className="flex-1 min-w-0 text-[10px]">
                <span className="text-slate-500">Set: </span>
                <span className="text-emerald-400 font-medium" data-testid="fastener-set-preview">
                  {[
                    setComponents.bolt && `${size}${lengthMm ? ' x ' + lengthMm : ''} ${selectedSubType?.name || 'Hex Head Bolt'}`,
                    setComponents.plain_washer && `${size} Plain Washer`,
                    setComponents.spring_washer && `${size} Spring Washer`,
                    setComponents.nut && `${size} Hex Nut`,
                  ].filter(Boolean).join(' + ')}
                  {selectedMaterial ? ` — ${selectedMaterial.name}` : ''}
                </span>
              </div>
            ) : previewDesc ? (
              <div className="flex-1 min-w-0 text-[10px]">
                <span className="text-slate-500">Preview: </span>
                <span className="text-cyan-400 font-medium" data-testid="fastener-preview-desc">{previewDesc}</span>
              </div>
            ) : <div className="flex-1" />}
            <div className="flex items-center gap-1.5 shrink-0">
              <Label className="text-[9px] text-slate-500">Qty (each):</Label>
              <Input type="number" min={1} value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                className="h-7 w-14 text-xs bg-slate-800 border-slate-600 text-white text-center"
                data-testid="fastener-quantity" />
              <Button size="sm"
                className={`h-7 text-[10px] text-white ${setMode ? 'bg-emerald-600 hover:bg-emerald-500' : 'bg-amber-600 hover:bg-amber-500'}`}
                onClick={handleSubmit}
                disabled={!canSubmit || submitting}
                data-testid="fastener-add-btn">
                {setMode ? <Package className="w-3 h-3 mr-0.5" /> : <Plus className="w-3 h-3 mr-0.5" />}
                {submitting ? 'Adding...' : (setMode ? 'Add Set to BOM' : 'Add to BOM')}
              </Button>
              {setMode && size && material && (
                <Button size="sm" variant="outline"
                  className="h-7 text-[10px] border-violet-600/50 text-violet-400 hover:bg-violet-900/30"
                  onClick={() => setShowSaveTemplate(!showSaveTemplate)}
                  data-testid="fastener-save-template-toggle">
                  <Save className="w-3 h-3 mr-0.5" /> Save as Template
                </Button>
              )}
              <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-400"
                onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>

          {/* Save Template Mini-Form (shown in set mode) */}
          {showSaveTemplate && setMode && (
            <div className="mt-1.5 p-2 rounded border border-violet-600/30 bg-violet-900/10 animate-in fade-in duration-150" data-testid="fastener-save-template-form">
              <div className="flex items-center gap-2">
                <Input value={templateName} onChange={e => setTemplateName(e.target.value)}
                  placeholder="Template name (e.g., Standard M8 Flange Set)"
                  className="h-7 text-xs bg-slate-800 border-violet-600/40 text-white flex-1"
                  onKeyDown={e => { if (e.key === 'Enter' && templateName.trim()) handleSaveTemplate(); }}
                  data-testid="fastener-template-name-input" autoFocus />
                <Input value={templateDesc} onChange={e => setTemplateDesc(e.target.value)}
                  placeholder="Description (optional)"
                  className="h-7 text-xs bg-slate-800 border-slate-600 text-white flex-1" />
                <Button size="sm" className="h-7 text-[10px] bg-violet-600 hover:bg-violet-500 text-white"
                  onClick={handleSaveTemplate} disabled={!templateName.trim()}
                  data-testid="fastener-save-template-submit">
                  <Save className="w-3 h-3 mr-0.5" /> Save
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-400"
                  onClick={() => { setShowSaveTemplate(false); setTemplateName(''); setTemplateDesc(''); }}>
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Empty state */}
      {!category && !setMode && !showCustomForm && (
        <div className="text-[9px] text-slate-600 text-center py-1">
          Select a fastener category above, or use "Add as Set" for bolt+nut+washer combos
        </div>
      )}

      {/* Custom Fastener Creation Form */}
      {showCustomForm && (
        <div className="mt-2 p-2 rounded border border-amber-600/30 bg-slate-900/50" data-testid="fastener-custom-form">
          <div className="text-[9px] text-amber-400 font-semibold uppercase tracking-wider mb-1.5">Create Custom Fastener</div>
          <div className="space-y-1.5">
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-[9px] text-slate-500 mb-0.5 block">Category</Label>
                <Select value={category || ''} onValueChange={v => setCategory(v)}>
                  <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-600 text-white" data-testid="custom-category">
                    <SelectValue placeholder="Category..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ref?.categories?.map(c => <SelectItem key={c.code} value={c.code}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <Label className="text-[9px] text-slate-500 mb-0.5 block">Sub-type Name</Label>
                <Input value={subType} onChange={e => setSubType(e.target.value)}
                  placeholder="e.g. T-Head Bolt, Shoulder Screw..."
                  className="h-7 text-xs bg-slate-800 border-slate-600 text-white"
                  data-testid="custom-subtype-input" />
              </div>
              <div className="w-24">
                <Label className="text-[9px] text-slate-500 mb-0.5 block">Size</Label>
                <Select value={size} onValueChange={setSize}>
                  <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-600 text-white">
                    <SelectValue placeholder="Size..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ref?.sizes?.map(s => <SelectItem key={s.size} value={s.size}>{s.size}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-[9px] text-slate-500 mb-0.5 block">Material</Label>
                <Select value={material} onValueChange={setMaterial}>
                  <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-600 text-white">
                    <SelectValue placeholder="Material..." />
                  </SelectTrigger>
                  <SelectContent>
                    {ref?.materials?.map(m => <SelectItem key={m.code} value={m.code}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-24">
                <Label className="text-[9px] text-slate-500 mb-0.5 block">Length (mm)</Label>
                <Input type="number" value={lengthMm} onChange={e => setLengthMm(e.target.value)}
                  placeholder="mm" className="h-7 text-xs bg-slate-800 border-slate-600 text-white" />
              </div>
              <div className="flex-1">
                <Label className="text-[9px] text-slate-500 mb-0.5 block">Standard</Label>
                <Input value={customStandard} onChange={e => setCustomStandard(e.target.value)}
                  placeholder="ISO/DIN/MIL..." className="h-7 text-xs bg-slate-800 border-slate-600 text-white"
                  data-testid="custom-standard-input" />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-[9px] text-slate-500 mb-0.5 block">Description Override (optional)</Label>
                <Input value={customDesc} onChange={e => setCustomDesc(e.target.value)}
                  placeholder="Full description (auto-generated if blank)"
                  className="h-7 text-xs bg-slate-800 border-slate-600 text-white"
                  data-testid="custom-desc-input" />
              </div>
              <div className="flex items-end gap-1.5">
                <Input type="number" min={1} value={quantity} onChange={e => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                  className="h-7 w-14 text-xs bg-slate-800 border-slate-600 text-white text-center" />
                <Button size="sm" className="h-7 text-[10px] bg-amber-600 hover:bg-amber-500 text-white"
                  onClick={async () => {
                    if (!category || !subType || !size || !material) {
                      toast.error('Category, type, size, and material are required');
                      return;
                    }
                    setSubmitting(true);
                    try {
                      const catObj = ref?.categories?.find(c => c.code === category);
                      const matObj = ref?.materials?.find(m => m.code === material);
                      const payload = {
                        category: catObj?.name || category,
                        sub_type: subType,
                        sub_type_name: subType,
                        size,
                        length_mm: lengthMm ? parseInt(lengthMm) : null,
                        thread_type: catObj?.has_thread ? threadType : null,
                        material,
                        material_name: matObj?.name || material,
                        material_group: matObj?.group || '',
                        finish: 'Plain',
                        finish_name: 'Plain',
                        standard: customStandard || '',
                        quantity,
                        is_custom: true,
                      };
                      await onAddFastener(payload);
                      toast.success(`Custom fastener added: ${customDesc || `${size} ${subType}`}`);
                      setShowCustomForm(false);
                      setCustomDesc('');
                      setSubType('');
                      setSize('');
                      setMaterial('');
                      setLengthMm('');
                      setCustomStandard('');
                      setQuantity(1);
                    } catch (e) {
                      toast.error(e.message || 'Failed');
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  disabled={!category || !subType || !size || !material || submitting}
                  data-testid="custom-fastener-submit">
                  <Plus className="w-3 h-3 mr-0.5" /> {submitting ? 'Adding...' : 'Add Custom'}
                </Button>
                <Button size="sm" variant="ghost" className="h-7 text-[10px] text-slate-400"
                  onClick={() => setShowCustomForm(false)}>Cancel</Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* "Can't find?" link */}
      {!showCustomForm && (category || setMode) && (
        <div className="text-center mt-1">
          <button onClick={() => { setShowCustomForm(true); setSetMode(false); }}
            className="text-[9px] text-amber-500/60 hover:text-amber-400 transition-colors"
            data-testid="fastener-custom-link">
            Can't find your fastener? Create a custom entry
          </button>
        </div>
      )}
    </div>
  );
};
